import { DateTimeStamp } from '@/types/dateTimeStamp';
import { range } from 'lodash';

export const quarters = [1, 2, 3, 4] as const;
export type Quarter = typeof quarters[number];

export interface QuarterDate {
	quarter: Quarter;
	year: number;
}

export function quarterToDate(quarterDate: QuarterDate): Date {
	const { quarter, year } = quarterDate;
	switch (quarter) {
		case 1:
			return new Date(`${year}-03-31`);
		case 2:
			return new Date(`${year}-06-30`);
		case 3:
			return new Date(`${year}-09-30`);
		case 4:
			return new Date(`${year}-12-31`);
	}
}

export function dateToQuarter(date: DateTimeStamp | Date): QuarterDate {
	let quarter: Quarter;
	switch (new Date(date).getUTCMonth()) {
		case 2:
			quarter = 1;
			break;
		case 5:
			quarter = 2;
			break;
		case 8:
			quarter = 3;
			break;
		case 11:
			quarter = 4;
			break;
		default:
			throw new Error(`Unknown date ${date} - must be a valid quarter`);
	}

	return {
		year: new Date(date).getUTCFullYear(),
		quarter: quarter,
	};
}

export function getPreviousQuarter(quarterDate: QuarterDate): QuarterDate {
	if (quarterDate.quarter === 1) {
		return {
			year: quarterDate.year - 1,
			quarter: 4,
		};
	}
	return {
		year: quarterDate.year,
		quarter: quarterDate.quarter - 1 as Quarter,
	};
}

export function quarterMinusQuarters(quarterDate: QuarterDate, quarters: number): QuarterDate {
	return range(quarters).reduce((currentDate, _) => getPreviousQuarter(currentDate), quarterDate);
}

export function quarterPlusQuarters(quarterDate: QuarterDate, quarters: number): QuarterDate {
	return range(quarters).reduce((currentDate, _) => getNextQuarter(currentDate), quarterDate);
}

export function getNextQuarter(quarterDate: QuarterDate): QuarterDate {
	if (quarterDate.quarter === 4) {
		return {
			year: quarterDate.year + 1,
			quarter: 1,
		};
	}
	return {
		year: quarterDate.year,
		quarter: quarterDate.quarter + 1 as Quarter,
	};
}

/**
 * Given a date get the quarter in the past that its closes to
 */
export function dateToLatestQuarter(date: Date): QuarterDate {
	const year = date.getUTCFullYear();

	if (date < quarterToDate({ year, quarter: 1 })) {
		return {
			year: year - 1,
			quarter: 4,
		};
	}

	if (date < quarterToDate({ year, quarter: 2 })) {
		return { year, quarter: 1 };
	}

	if (date < quarterToDate({ year, quarter: 3 })) {
		return { year, quarter: 2 };
	}

	if (date < quarterToDate({ year, quarter: 4 })) {
		return { year, quarter: 3 };
	}

	throw new Error('Unable to get current quarter');
}

export function quarterIsLessThan(quarterLeft: QuarterDate, quarterRight: QuarterDate) {
	if (quarterLeft.year === quarterRight.year) {
		return quarterLeft.quarter < quarterRight.quarter;
	}

	return quarterLeft.year < quarterRight.year;
}

export function quartersAreEqual(quarterLeft: QuarterDate, quarterRight: QuarterDate) {
	return quarterLeft.year === quarterRight.year && quarterLeft.quarter === quarterRight.quarter;
}

export function quarterInclusiveRange(startQuarter: QuarterDate, endQuarter: QuarterDate): QuarterDate[] {
	let currentQuarter = startQuarter;
	const quarters: QuarterDate[] = [currentQuarter];

	while (quarterIsLessThan(currentQuarter, endQuarter)) {
		currentQuarter = getNextQuarter(currentQuarter);
		quarters.push(currentQuarter);
	}

	return quarters;
}

export function formatQuarter(quarterDate: QuarterDate): string {
	return `Q${quarterDate.quarter} ${quarterDate.year}`;
}
